import {
  COOKIE_OID,
  getCookie,
  getUserIpFromCookieInWebCatalog,
} from '@utils/cookies';
import {
  getFallbackDeviceType,
  parseUserAgent,
  UserAgentData,
} from '@utils/parseUserAgent';
import {safeLocalStorage} from '@utils/safeStorages';
import {
  SendCustomEventsBatchRequest,
  sendStatsBatch,
} from '@utils/sendStatsBatch';
import {useCallback, useEffect, useRef} from 'react';
import {v4} from 'uuid';

import {AdFoxStatsEvent} from '../../types';
import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from '../YandexAdsContext';

const DEFAULT_INTERVAL_MS = 5000;

const convertEvents = (
  events: AdFoxStatsEvent[],
  userId: string,
  commonParams: Record<string, string>,
): SendCustomEventsBatchRequest => ({
  events: events.map(({eventName, payload}) => ({
    event_name: eventName,
    params: {...commonParams, ...payload},
    user_id: userId,
  })),
});

/**
 * периодически забирает накопившиеся ивенты и сливает их в ручку,
 *
 * также добавляет к событиям общие для всех ивентов параметры
 */
export const AdFoxEventsSender = () => {
  const {consumeAdFoxEvents, statsUrl, getUserVkId} =
    useYandexAdsContextSelector(YandexAdsContextCommonSelectors.getFullState);

  const uidRef = useRef('');
  useEffect(() => {
    uidRef.current = safeLocalStorage.getItem('sspUserId') || v4();
  }, []);

  const sessionIdRef = useRef(v4());
  const deviceDataRef = useRef<UserAgentData>(null);

  useEffect(() => {
    parseUserAgent().then((deviceData) => {
      deviceDataRef.current = deviceData;
    });
  }, []);

  const getCommonParams = useCallback(() => {
    const deviceData = deviceDataRef.current;
    return {
      user_id: uidRef.current,
      session_id: sessionIdRef.current,
      vk_id: `${getUserVkId()}`,
      oid: getCookie(COOKIE_OID) || 'not_set',
      ip: getUserIpFromCookieInWebCatalog() || 'not_set',
      device_type: deviceData?.deviceType ?? getFallbackDeviceType(),
      browser_type:
        [deviceData?.browserName, deviceData?.browserVersion]
          .filter(Boolean)
          .join(' ') || 'unknown',
      device_os: deviceData?.osName || 'unknown',
    };
  }, [getUserVkId]);

  useEffect(() => {
    const unloadHandler = () => {
      const stats = consumeAdFoxEvents();
      if (stats.length) {
        sendStatsBatch(
          convertEvents(stats, uidRef.current, getCommonParams()),
          statsUrl,
          true,
        );
      }
    };

    window.addEventListener('beforeunload', unloadHandler);
    return () => {
      window.removeEventListener('beforeunload', unloadHandler);
    };
  }, [statsUrl, consumeAdFoxEvents, getCommonParams]);

  useEffect(() => {
    const interval = setInterval(() => {
      const stats = consumeAdFoxEvents();
      if (stats.length) {
        sendStatsBatch(
          convertEvents(stats, uidRef.current, getCommonParams()),
          statsUrl,
        );
      }
    }, DEFAULT_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [statsUrl, consumeAdFoxEvents, getCommonParams]);

  return null;
};
